import React, { useRef, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { getTranslation } from "../common";
import { SUPPORTED_EXTENSIONS, getFileExtension } from "common/utilities";
import { useSelectedOrganization, useSnackbar, useTranslations, useUser } from "hooks";
import { Upload } from "@mui/icons-material";
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { useNavigate } from "react-router-dom";
import FlowentiButton from "./flowenti-ui-kit/FlowentiButton";
import PendingFileList from "./PendingFileList";


interface DragDropProps {
  onFileChange: (files: File[]) => void;
  files: File[];
  maxFiles?: number;
}
const DropZone: React.FC<DragDropProps> = ({ onFileChange, files, maxFiles }) => {
  // const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const translations = useTranslations();
  const user = useUser();
  const locale = user?.i18n || navigator.language.split("-")[0];
  const { bypassSubscription, storageDiskCanUse, refreshSubscriptionFeatures, storageUsage, checkLoading } = useSubscriptionFeatures();
  const organization = useSelectedOrganization()
  const navigate = useNavigate()
  const { sendSnack } = useSnackbar();
  const pastedFilesRef = useRef<Set<string>>(new Set());

  const uploadFiles = async (newFiles: File[]) => {
    if (newFiles.length === 0) return;

    const uniqueNewFiles: File[] = [];
    for (const newFile of newFiles) {
      const ext = getFileExtension(newFile.name);
      if (!ext || !SUPPORTED_EXTENSIONS.includes(ext.toLowerCase())) {
        sendSnack({
          message: getTranslation(translations, "attachment.extension.unsupported") || `L'estensione del file non è supportata.`,
          type: "error",
        });
        continue;
      }
      if (files.some(f => f.name === newFile.name && f.size === newFile.size)) {
        sendSnack({
          message: `${getTranslation(translations, "attachment.already.present") || "File già presente"}: ${newFile.name}`,
          type: "info",
        });
      } else {
        uniqueNewFiles.push(newFile);
      }
    }

    if (uniqueNewFiles.length === 0) return;

    const fileSize = uniqueNewFiles.reduce((acc, file) => acc + (file?.size || 0), 0)
    const totalFiles = files.length + uniqueNewFiles.length;

    if (maxFiles && totalFiles > maxFiles) {
      sendSnack({
        message: `puoi allegare al massimo ${maxFiles} file.`,
        type: "error",
      });
      return;
    }
    if (!bypassSubscription) {
      const usage = (storageUsage?.using || 0) + files.reduce((acc, file) => acc + file?.size, 0)
      if ((fileSize + usage) <= (storageUsage?.permit || 0)) {
        onFileChange([...files, ...uniqueNewFiles]);
        refreshSubscriptionFeatures();
      } else {
        sendSnack({
          message: "usage exceeded",
          type: "error",
        });
      }
    } else {
      onFileChange([...files, ...uniqueNewFiles]);
      refreshSubscriptionFeatures();
    }
  }


  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const newFiles = Array.from(event.dataTransfer.files);
    uploadFiles(newFiles)
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      uploadFiles(newFiles)
    }
  };

  const uploadFilesRef = useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;

  useEffect(() => {
    const handleGlobalPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (items) {
        const pastedFiles: File[] = [];
        const now = new Date();

        // Build locale-aware date string by reading Intl part order
        const parts = new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).formatToParts(now);
        const dateParts: Record<string, string> = {};
        parts.forEach((p) => { if (p.type !== "literal") dateParts[p.type] = p.value; });
        // Preserve locale order (e.g. day/month/year or year/month/day)
        const dateOrdered = parts
          .filter((p) => p.type !== "literal")
          .map((p) => dateParts[p.type])
          .join("-");

        const timeStr = [
          String(now.getHours()).padStart(2, "0"),
          String(now.getMinutes()).padStart(2, "0"),
          String(now.getSeconds()).padStart(2, "0"),
        ].join("-");
        const timestamp = `${dateOrdered}_${timeStr}`;

        for (let i = 0; i < items.length; i++) {
          if (items[i].kind === "file") {
            const file = items[i].getAsFile();
            if (file) {
              const pasteKey = `${file.name}-${file.size}`;
              if (pastedFilesRef.current.has(pasteKey)) {
                sendSnack({
                  message: getTranslation(translations, "attachment.already.pasted") || "Allegato già incollata",
                  type: "info",
                });
                continue;
              }
              pastedFilesRef.current.add(pasteKey);
              const ext = file.type ? `.${file.type.split("/")[1]}` : "";
              const suffix = pastedFiles.length > 0 ? `_${pastedFiles.length + 1}` : "";
              const renamedFile = new File([file], `${timestamp}${suffix}${ext}`, { type: file.type });
              pastedFiles.push(renamedFile);
            }
          }
        }
        if (pastedFiles.length > 0) {
          event.preventDefault();
          uploadFilesRef.current(pastedFiles);
        }
      }
    };


    document.addEventListener("paste", handleGlobalPaste);
    return () => {
      document.removeEventListener("paste", handleGlobalPaste);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    return () => {
      // setFiles([]);
      onFileChange([]);
      refreshSubscriptionFeatures()
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (<>
    {checkLoading ? <CircularProgress /> :
      <div>
        {!bypassSubscription && !storageDiskCanUse ?
          <Box display="flex" alignItems="center"><Alert sx={{ height: 40, alignItems: "center", overflowY: "hidden", mr: 2 }} severity="error">{getTranslation(translations, "storage.limit.reached")}
          </Alert>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/dashboard/organizations/${organization?.id}/billing`, { state: { organizationName: organization?.name } })}
            >
              {getTranslation(translations, "generic.upgrade")}
            </Button>
          </Box> : <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{
              border: "2px dashed #ccc",
              borderRadius: "10px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              transition: "border-color 0.2s ease-in-out",
            }}
            onClick={() => {
              fileInput.current?.click();
            }}
            onMouseOver={(e) => (e.currentTarget.style.borderColor = "#54bdc1")}
            onMouseOut={(e) => (e.currentTarget.style.borderColor = "#ccc")}
          >
            <Typography sx={{ pb: 4 }} variant="body1">
              {getTranslation(translations, "dropzone.drag_drop_caption")}
            </Typography>
            <input
              type="file"
              multiple
              accept={SUPPORTED_EXTENSIONS.map(ext => `.${ext}`).join(',')}
              onChange={handleFileInputChange}
              style={{ display: "none" }}
              ref={fileInput}
            />
            <FlowentiButton
              id="upload-attachment-button"
              size="small"
              startIcon={<Upload />}
              onClick={(e) => {
                e.stopPropagation();
                fileInput.current?.click();
              }}
              sx={{
                backgroundColor: "FFFFFF",
                border: "1px solid #DEE2E6",
                "&:hover": {
                  backgroundColor: "",
                },
              }}
            >
              {getTranslation(translations, "generic.select_file.caption")}
            </FlowentiButton>
          </div>}
        <PendingFileList files={files} onChange={onFileChange} />
      </div>}
  </>
  );
};

export default DropZone;
